'use client';
/* eslint-disable max-len */
import {
  Dialog,
  DialogPanel,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';

import classNames from 'classnames';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  Ads,
  Alert,
  Button,
  Close,
  DeliveryFee,
  FreeDeliveryAlert,
  Icon,
  IconSvg,
  Link,
  useAuth,
  useData,
} from '@/components';
import {
  useAnalytics,
  useEcomStoreSelector,
  useUiStoreSelector,
  useUpdateTreezCart,
} from '@/data';
import {
  useCurrentMinimumAmount,
  useDeliveryAddressZone,
  useFeaturedFlag,
  useIsServerSide,
  useSelectedStore,
  useSummary,
} from '@/hooks';
import {
  AdsSlice,
  ErrorResponse,
  ErrorResponseCodeType,
  TreezSpecial,
} from '@/types';
import {
  currencyFormat,
  formatInsufficientSellableQuantityMessage,
  getStoreOrderMetadata,
  getTaxValue,
  mapLineItems,
} from '@/utils';

import CartBody from './CartBody';
import { CartShoppingInfo } from './CartShoppingInfo';
import Discount from './Discount';
import { SummaryTotalSkeleton } from './SummaryTotalSkeleton';

import style from './cartnavigation.module.scss';

const GO_TO_CHECKOUT_BTN_LABEL = 'Proceed to checkout';

enum UpdateCartErrorType {
  _BackendError = 'BACKEND_ERROR',
  _ApolloError = 'APOLLO_ERROR',
}

const CartNavigation: FC = () => {
  const { goToSignIn, goToCheckout, isAuth } = useAuth();
  const { isServerSide } = useIsServerSide();
  const { isTaxAppliedMessage: displayPostTax, allowGuestCheckout } =
    useFeaturedFlag();

  const {
    berbix_verified,
    line_items,
    treez_store_name,
    delivery_details,
    delivery_address,
    entity_id,
    treez_pay_ticket_ids_by_store,
    total_delivery,
    setState: setStoredState,
    allow_custom_delivery_fee,
  } = useEcomStoreSelector([
    'berbix_verified',
    'treez_store_name',
    'line_items',
    'delivery_details',
    'delivery_address',
    'entity_id',
    'treez_pay_ticket_ids_by_store',
    'total_delivery',
    'allow_custom_delivery_fee',
  ]);

  const { is_cart_open, setState } = useUiStoreSelector(['is_cart_open']);
  const isDeliveryAddressOutOfZone = useDeliveryAddressZone();
  const { apps, shoppingCart } = useData();
  const {
    serverSubtotal,
    preDiscountSubtotal,
    subtotal,
    totalGrams,
    tax,
    discounts,
    total,
  } = useSummary();
  const { currentMinAmount, delivery_schedules } = useCurrentMinimumAmount();
  const { store } = useSelectedStore();

  const isBerbixAppEnabled = useMemo(
    () => !!apps?.find(({ handler }) => handler === 'berbix'),
    [apps],
  );
  const needsBerbixValidation = useMemo(
    () => isBerbixAppEnabled && delivery_details && !berbix_verified,
    [delivery_details, berbix_verified],
  );
  const { measureViewCartGA4, measureSurfsideAction } = useAnalytics();

  // local state
  const [loading, setLoading] = useState(false);

  const [failedToLoadCartTotals, setFailedToLoadCartTotals] = useState<{
    error?: ErrorResponse | null;
    status?: UpdateCartErrorType;
  }>({
    error: undefined,
    status: undefined,
  });

  const hasServerTotals =
    !!serverSubtotal &&
    failedToLoadCartTotals?.status !== UpdateCartErrorType._ApolloError;
  // The backend adds the delivery fee we send to subtotal_price,
  // so we subtract it to show and use the real cart subtotal
  const deliveryFeeInSubtotal =
    hasServerTotals &&
    !failedToLoadCartTotals?.status &&
    store?.allow_custom_delivery_fee &&
    (total_delivery ?? 0) > 0
      ? delivery_schedules?.[0]?.delivery_fee ?? 0
      : 0;
  const cartSubtotal = serverSubtotal - deliveryFeeInSubtotal;
  const orderTotal = hasServerTotals ? cartSubtotal - discounts : subtotal;

  // Cart error handling
  const cartError = useMemo(() => {
    if (!failedToLoadCartTotals?.status || !failedToLoadCartTotals?.error) {
      return null;
    }

    const errorMessage = failedToLoadCartTotals.error?.message;
    const errorCode = failedToLoadCartTotals.error?.code;

    switch (errorCode) {
      case ErrorResponseCodeType.InsufficientSellableQuantity:
        return {
          message: formatInsufficientSellableQuantityMessage(
            line_items ?? [],
            errorMessage,
          ),
          blockCheckout: true,
        };

      case ErrorResponseCodeType.DeliveryMinimumError:
        return {
          message: errorMessage,
          blockCheckout: true,
        };
    }

    if (
      errorMessage?.includes(
        'All of the following fields must be present in delivery_address',
      )
    ) {
      return {
        message:
          'You must select a store or enter a delivery address to proceed',
        blockCheckout: true,
      };
    }

    return null;
  }, [
    failedToLoadCartTotals?.status,
    failedToLoadCartTotals?.error,
    line_items,
  ]);

  const [specials, setSpecials] = useState<TreezSpecial[] | undefined>(
    undefined,
  );
  const [checkoutValidation, setCheckoutValidation] = useState({
    disabled: false,
    label: GO_TO_CHECKOUT_BTN_LABEL,
  });
  const firstUpdateTreezCart = useRef(true);
  const lastStringifiedItemsWhenOpen = useRef<string>('');
  const [isSilentCartUpdate, setIsSilentCartUpdate] = useState(false);

  const heading = shoppingCart?.heading ?? 'Shopping cart';
  const buttonColor = shoppingCart?.button_color ?? 'primary';
  const message = shoppingCart?.free_delivery_message;
  const messageCompleted = shoppingCart?.free_delivery_activation_message;
  const ad = shoppingCart?.slices?.find((a: AdsSlice) => a?.primary?.active);

  const isPickup = delivery_details?.pickup;

  // Update allow_custom_delivery_fee (local storage) based on store settings and fulfillment method
  useEffect(() => {
    const allowCustomDeliveryFee =
      !isPickup && !!store?.allow_custom_delivery_fee;

    if (allow_custom_delivery_fee !== allowCustomDeliveryFee) {
      setStoredState({
        allow_custom_delivery_fee: allowCustomDeliveryFee,
      });
    }
  }, [isPickup, store?.allow_custom_delivery_fee, allow_custom_delivery_fee]);

  // Calculate delivery fee reactively based on current subtotal
  const desiredDeliveryFee = useMemo(() => {
    const currentDelivery = total_delivery ?? 0;

    // If pickup mode, no delivery fee
    if (isPickup) {
      return 0;
    }

    // If custom delivery fee is not allowed, keep the current stored value
    if (!store?.allow_custom_delivery_fee) {
      return currentDelivery;
    }

    // Get the delivery fee configuration from the first delivery schedule
    const deliveryFeeCfg = delivery_schedules?.[0]?.delivery_fee ?? 0;
    const freeAfter = delivery_schedules?.[0]?.free_after ?? -1;

    // Determine what the fee should be based on subtotal
    const shouldBeFree = freeAfter > 0 && (orderTotal ?? 0) >= freeAfter;
    const calculatedFee = shouldBeFree ? 0 : deliveryFeeCfg;

    return calculatedFee;
  }, [
    isPickup,
    store?.allow_custom_delivery_fee,
    delivery_schedules,
    orderTotal,
    total_delivery,
  ]);

  // this update treez cart is only to calculate the totals
  // and display it in the cart

  const order = {
    entity_id: entity_id,
    email: '', // in the future we will have the email here so we can also display group discounts at the cart level
    line_items: mapLineItems(line_items),
    provider_store_full_address: store?.full_address,
    provider_store_id: store?.short_name,
    provider_store_name: store?.name,
    provider_store_phone_number: store?.phone,
    delivery_details,
    delivery_address,
    treez_pay_ticket_ids_by_store: treez_pay_ticket_ids_by_store,
    allow_custom_delivery_fee: !isPickup && store?.allow_custom_delivery_fee,
    total_delivery: desiredDeliveryFee,
    ...getStoreOrderMetadata(store),
  };

  const {
    updateCart,
    result: { loading: isFetchingTotals },
  } = useUpdateTreezCart({
    variables: {
      input: {
        order: order,
      },
    },
    onCompleted: data => {
      const res = data?.updateTreezCart;
      if (!res) {
        return;
      }
      firstUpdateTreezCart.current = false;
      setIsSilentCartUpdate(false);

      const { status, specials, order, error } = res;

      if (!status) {
        setFailedToLoadCartTotals({
          error,
          status: UpdateCartErrorType._BackendError,
        });

        const total_tax = error?.data?.tax_total ?? 0;
        const subtotal_price = displayPostTax
          ? error?.data?.post_tax_subtotal ?? 0
          : error?.data?.sub_total ?? 0;
        const total_delivery = error?.data?.delivery_fee ?? 0;
        const total_discounts = displayPostTax
          ? error?.data?.post_tax_discount_total ?? 0
          : error?.data?.discount_total ?? 0;

        setStoredState({
          total_tax,
          total_discounts,
          subtotal_price,
          total_delivery,
        });
        return;
      }

      // set the specials on the local state
      setSpecials(specials as any);

      // persist the order total values on the local state
      setStoredState({
        entity_id: order?.entity_id as string,
        total_discounts: order?.total_discounts as number,
        total_tax: order?.total_tax as number,
        total_price: order?.total_price as number,
        subtotal_price: order?.subtotal_price as number,
        total_delivery: order?.total_delivery ?? 0,
        total_service_fee: order?.total_service_fee ?? 0,

        //treez order
        treez_order_id: order?.treez_order_id,
        treez_order_number: order?.treez_order_number,
        treez_pay_ticket_ids_by_store:
          order?.treez_pay_ticket_ids_by_store ?? [],
      });

      setFailedToLoadCartTotals({ error: undefined, status: undefined });
    },
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error?.message);
      setFailedToLoadCartTotals({
        error: { message: error?.message, code: ErrorResponseCodeType.Unknown },
        status: UpdateCartErrorType._ApolloError,
      });
    },
  });

  // validate the delivery minimum amount
  useEffect(() => {
    if (!treez_store_name) {
      return;
    }

    if (isPickup) {
      setCheckoutValidation({
        disabled: false,
        label: GO_TO_CHECKOUT_BTN_LABEL,
      });
      return;
    }

    if (currentMinAmount === 0) {
      setCheckoutValidation({
        disabled: false,
        label: GO_TO_CHECKOUT_BTN_LABEL,
      });
      return;
    }

    const minimumTotal = store?.delivery_minimum_total ? total : orderTotal;
    const minimumTotalLabel = store?.delivery_minimum_total
      ? 'Grand Total'
      : 'Subtotal';
    const underCurrentMinAmount = currentMinAmount > minimumTotal;

    setCheckoutValidation({
      disabled: underCurrentMinAmount,
      label: underCurrentMinAmount
        ? `$${currencyFormat(
            currentMinAmount,
          )} ${minimumTotalLabel} Min. For Delivery`
        : GO_TO_CHECKOUT_BTN_LABEL,
    });
  }, [delivery_details, orderTotal, treez_store_name, currentMinAmount, total]);

  // Go to checkout button event
  const handleProceed = async () => {
    if (isDeliveryAddressOutOfZone) {
      setStoredState({ temporary_fulfillment_method: 'DELIVERY' });
      setState({ is_cart_open: false, is_store_location_open: true });
      return;
    }

    if (!allowGuestCheckout && (!isAuth || needsBerbixValidation)) {
      setState({ is_cart_open: false });
      setStoredState({ checkout_identity_verification: true });
      await goToSignIn();
      return;
    }

    setLoading(true);

    await goToCheckout();
    setState({ is_cart_open: false });
    setLoading(false);
  };

  useEffect(() => {
    if (is_cart_open && line_items?.length) {
      measureViewCartGA4(line_items);
      measureSurfsideAction(line_items, 'cart');
    }
  }, [is_cart_open]);

  const stringifiedItems = JSON.stringify(line_items);

  useEffect(() => {
    if (!is_cart_open) {
      if (firstUpdateTreezCart.current && line_items?.length) {
        firstUpdateTreezCart.current = false;
        updateCart();
      }
      return;
    }

    const itemsChanged =
      stringifiedItems !== lastStringifiedItemsWhenOpen.current;
    lastStringifiedItemsWhenOpen.current = stringifiedItems;

    const timeout = setTimeout(() => {
      if (itemsChanged) {
        setIsSilentCartUpdate(false);
        updateCart();
      } else {
        setIsSilentCartUpdate(true);
        updateCart();
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [is_cart_open, stringifiedItems, line_items?.length]);

  // Reconciliation effect: trigger update when delivery fee should change
  // after server applies bundle discounts that affect subtotal
  useEffect(() => {
    // Skip if cart is closed, no items, or currently fetching totals
    if (!is_cart_open || !line_items?.length) {
      return;
    }

    // Skip if desired fee matches current stored fee
    if ((total_delivery ?? 0) === desiredDeliveryFee) {
      return;
    }

    // Debounce the reconciliation to prevent multiple rapid updates
    const timeout = setTimeout(() => {
      updateCart();
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [total_delivery, desiredDeliveryFee]);

  const [isExpanded, setIsExpanded] = useState(false);

  if (isServerSide) return null;

  // Silent update = refresh on open (same items); don't show skeletons
  const showCartLoading = isFetchingTotals && !isSilentCartUpdate;

  // Show partial totals (subtotal, discounts, tax, delivery) when we have data from success OR BackendError
  const displayTotals =
    line_items?.length > 0 &&
    !showCartLoading &&
    (!failedToLoadCartTotals?.status ||
      failedToLoadCartTotals?.status === UpdateCartErrorType._BackendError);

  // Show payment total only when cart update was fully successful
  const showPaymentTotal =
    line_items?.length > 0 &&
    !showCartLoading &&
    !failedToLoadCartTotals?.status;

  const displayTaxDiscountAtCheckout =
    line_items?.length > 0 &&
    !showCartLoading &&
    failedToLoadCartTotals?.status === UpdateCartErrorType._ApolloError;

  const freeAfter = delivery_schedules?.[0]?.free_after ?? 0;

  const footerItemClass = (isExpanded: boolean, className?: string) =>
    classNames(className, style.footer__item_hide_mobile, {
      [style.footer__item_expanded]: isExpanded,
    });

  const TotalWeightSection = () => (
    <div
      aria-label={`Total Weight: ${totalGrams?.toFixed(2)} G`}
      className={footerItemClass(isExpanded, style.footer__grams_container)}
      role="group"
    >
      <span className={style.heading}>
        Total Weight{' '}
        <Popover className={style.footer__help}>
          <PopoverButton aria-label="Show total weight information">
            <Icon name="help" />
          </PopoverButton>
          <PopoverPanel className={style.footer__help_body}>
            <div className={style.peak}></div>
            Total Weight is calculated only for products measured in grams—like
            flower, pre-rolls, cartridges, and extracts. Items such as edibles,
            beverages, tinctures, and topicals are typically excluded. This
            helps ensure compliance with legal purchase limits.
          </PopoverPanel>
        </Popover>
      </span>
      <span aria-hidden="true" className={style.grams}>
        {totalGrams?.toFixed(2)} G
      </span>
    </div>
  );

  const SubtotalSection = () => {
    const subtotalAmount = `$${
      displayTotals
        ? currencyFormat(cartSubtotal)
        : currencyFormat(preDiscountSubtotal)
    }`;

    return (
      <div
        aria-label={`Subtotal: ${subtotalAmount}`}
        className={classNames(style.footer__grams_container, {
          [style.footer__grams_container_subtotal]:
            showCartLoading || failedToLoadCartTotals?.status,
        })}
        role="group"
      >
        <span className={style.heading}>Subtotal</span>
        <span aria-hidden="true" className={style.grams}>
          {subtotalAmount}
        </span>
      </div>
    );
  };

  const DiscountsSection = () => (
    <>
      {discounts > 0 && (
        <Discount
          cartBackendError={
            failedToLoadCartTotals?.status === UpdateCartErrorType._BackendError
          }
          discounts={discounts}
          specials={specials}
        />
      )}
    </>
  );

  const OrderTotalSection = () => (
    <p
      aria-label={`Order total: $${currencyFormat(orderTotal)}`}
      className={footerItemClass(isExpanded, style.post_discount_subtotal)}
      role="group"
    >
      Order Total
      <span aria-hidden="true" className={style.post_discount_subtotal_value}>
        ${currencyFormat(orderTotal)}
      </span>
    </p>
  );

  const DeliveryFeeProgress = () => (
    <>
      {!isPickup && (
        <>
          <DeliveryFee
            classname={footerItemClass(isExpanded)}
            isLoading={showCartLoading}
            totalDelivery={total_delivery}
          />
          {!cartError && (
            <FreeDeliveryAlert
              classname={footerItemClass(isExpanded, style.delivery_alert)}
              isLoading={showCartLoading}
              message={message}
              messageCompleted={messageCompleted}
              minDeliveryAmount={freeAfter}
              subtotal={orderTotal}
              totalDelivery={total_delivery}
            />
          )}
        </>
      )}
    </>
  );

  const TaxSection = () => {
    const taxValue = getTaxValue(displayPostTax, tax);

    return (
      <div
        aria-label={`Taxes: ${taxValue}`}
        className={footerItemClass(isExpanded, style.footer__grams_container)}
        role="group"
      >
        <span className={style.heading}>Taxes</span>
        <span aria-hidden="true" className={style.grams}>
          {taxValue}
        </span>
      </div>
    );
  };

  const PaymentTotalSection = () => {
    const totalAmount = `$${currencyFormat(total)}`;

    if (!showPaymentTotal) return null;

    return (
      <p aria-label={`Total payment: ${totalAmount}`} role="group">
        <strong>Payment total </strong>
        <span aria-hidden="true" className={style.subtotal}>
          {totalAmount}
        </span>
      </p>
    );
  };

  const TaxDiscountAtCheckoutSection = () => (
    <>
      {displayTaxDiscountAtCheckout && (
        <small className={style.tax_info}>
          (tax and discounts applied at check-out)
        </small>
      )}
    </>
  );

  const CheckoutButton = () => (
    <Button
      aria-disabled={line_items?.length == 0 || checkoutValidation.disabled}
      className={style.proceed}
      color={buttonColor}
      disabled={
        line_items?.length == 0 ||
        checkoutValidation.disabled ||
        cartError?.blockCheckout ||
        isFetchingTotals
      }
      loading={loading}
      onClick={handleProceed}
      size="lg"
    >
      <div className={style.proceed_label}>
        {checkoutValidation.label}
        <IconSvg name="arrow-right" />
      </div>
    </Button>
  );

  const Summary = () => (
    <div className={style.cart__footer}>
      <button
        aria-label={isExpanded ? 'Collapse summary' : 'Expand summary'}
        className={style.summary__handle}
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
      >
        <div className={style.summary__handle_more_info}>
          <IconSvg name="shape-container" />
          <span
            className={classNames(style.summary__handle_arrow, {
              [style.summary__handle_arrow_expanded]: isExpanded,
            })}
          >
            <Icon name="angle-up" />
          </span>
        </div>
      </button>
      <div className={style.footer__current_shopping}>
        <TotalWeightSection />
        <SubtotalSection />

        {showCartLoading && <SummaryTotalSkeleton elements={3} />}

        {displayTotals && (
          <>
            <DiscountsSection />
            <OrderTotalSection />
          </>
        )}

        {delivery_details?.delivery && <DeliveryFeeProgress />}

        {showCartLoading && (
          <SummaryTotalSkeleton
            classname={footerItemClass(isExpanded)}
            elements={2}
          />
        )}

        {displayTotals && (
          <>
            <TaxSection />
            <PaymentTotalSection />
          </>
        )}

        <TaxDiscountAtCheckoutSection />
        <CheckoutButton />
      </div>
    </div>
  );

  return (
    <Dialog
      aria-label={heading}
      aria-labelledby="cart-title"
      aria-modal="true"
      className={classNames(style.cart_nav, {
        [style.cart_nav__open]: is_cart_open,
      })}
      onClose={() => setState({ is_cart_open: false })}
      open={is_cart_open}
      role="dialog"
      static
      tabIndex={-1}
    >
      <DialogPanel className={style.cart_nav__container}>
        <div className={style.cart_nav__body}>
          <div className={style.cart__header}>
            <div className={style.cart__shopping}>
              <Link
                aria-label="Return to homepage"
                href="/"
                onClick={() => setState({ is_cart_open: false })}
              >
                <h2 className={style.cart__shopping_link} id="cart-title">
                  {heading}
                </h2>
              </Link>
              <Close
                aria-label="Close shopping cart"
                onClick={() => setState({ is_cart_open: false })}
              />
            </div>
            <CartShoppingInfo />
          </div>
          {cartError && (
            <Alert error={true} isOpen={true} noAction={true}>
              <span>{cartError.message}</span>
            </Alert>
          )}
          {line_items?.length == 0 && (
            <div className={style.cart__empty}>
              {/* <Icon className={style.empty_cart_icon} name="empty-cart-icon" /> */}
              <figure className={style.empty_cart_icon}>
                <IconSvg aria-hidden="true" name="empty-shopping-cart" />
              </figure>
              <p style={{ width: '100%' }}>
                Your bag is empty <br /> Let’s go shopping now!
              </p>
            </div>
          )}
          {line_items?.length > 0 && <CartBody products={line_items} />}
        </div>
        {ad && <Ads {...ad} />}
        <Summary />
      </DialogPanel>
      <div aria-hidden="true" className={style.cart_nav__overlap} />
    </Dialog>
  );
};

export default CartNavigation;
